import moment from 'moment';
import { debounce } from 'lodash';
import {
    Button,
    DatePicker,
    Form,
    Input,
    Modal,
    Select,
    Space,
    Typography,
    Transfer,
} from 'antd';
import { useCallback, useState } from 'react';
import { WorkspaceInfo } from '../../../types/bookingRequests';
import { getAssociatedBusiness, getWorkspaces } from '../../../api/workspaces';

interface IProps {
    visible: boolean;
    onCancel: () => void;
    onOk: (values: any) => void;
}

const { Text } = Typography;
const { TextArea } = Input;

const NewInvoice = ({ visible, onCancel, onOk }: IProps) => {
    const [form] = Form.useForm();
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [business, setBusiness] = useState<any[]>([]);
    const [loadingBusiness, setLoadingBusiness] = useState(false);
    const [targetKeys, setTargetKeys] = useState<string[]>([]);
    const [limit] = useState<number>(10);
    const [options, setOptions] = useState<any[]>([]);

    const INVOICE_TYPES = [
        {
            label: 'MRR_PARTIAL',
            value: 'MRR_PARTIAL',
        },
    ];

    const RULES = [
        {
            required: true,
            message: 'This field is required',
        },
    ];

    const fetchWorkspaces = (search?: string, page?: number) => {
        setLoading(true);

        const params: any = {
            limit,
            page,
        };

        if (search) {
            params.search = search;
        }

        getWorkspaces({ paginator: params })
            .then(({ data }: any) => {
                setOptions(data.data.map((workspace: WorkspaceInfo) => ({
                    label: `${workspace.name} - ${workspace.workspaceUniqueID}`,
                    value: workspace._id,
                })));
            })
            .catch((err) => console.log('Error fetching workspaces', err))
            .finally(() => setLoading(false));
    };

    const fetchBusiness = (workspaceID: string) => {
        setLoadingBusiness(true);
        getAssociatedBusiness(workspaceID)
            .then(({ data }: any) => {
                const dataMapped = data.body.data.map((business: any) => ({
                    key: business._id,
                    companyName: business.businessID.companyName,
                }));
                setBusiness(dataMapped);
            })
            .catch((err) => console.log('Error fetching business', err))
            .finally(() => setLoadingBusiness(false));
    };

    const fetchDebounced = useCallback<any>(debounce(fetchWorkspaces, 700), []);

    const handleSearch = (newValue: string) => {
        if (newValue) {
            fetchDebounced(newValue, 1);
            setSearch(newValue);
        } else {
            setOptions([]);
        }
    };

    const onFinish = (values: any) => {
        const payload = {
            ...values,
            amount: Number(values.amount),
            date: moment(values.date).toISOString(),
        };
        (!payload.comment) && delete payload.comment;
        onOk(payload);
        form.resetFields();
        setTargetKeys([]);
        setOptions([]);
        onCancel();
    };

    const handleChange = (newValue: string) => fetchBusiness(newValue);

    return (
        <Modal
            title="New Invoice"
            width={800}
            open={visible}
            onCancel={onCancel}
            footer={null}
        >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        type: INVOICE_TYPES[0].value,
                        date: moment(),
                    }}
                    onFinish={onFinish}
                >
                    <Form.Item label="Workspace" name="workspaceID" rules={RULES}>
                        <Select
                            loading={loading}
                            filterOption={false}
                            showArrow={false}
                            defaultActiveFirstOption={false}
                            showSearch
                            allowClear
                            placeholder="Search workspace"
                            onSearch={handleSearch}
                            onChange={handleChange}
                            options={options}
                            value={search}
                        />
                    </Form.Item>
                    <Form.Item label="Invoice Type" name="type" rules={RULES}>
                        <Select
                            style={{ width: '100%' }}
                            options={INVOICE_TYPES}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Invoice Date"
                        name="date"
                        rules={RULES}
                        help="Date when the invoice was created"
                    >
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        name="amount"
                        label="Amount"
                        rules={RULES}
                    >
                        <Input type="number" step="0.01" />
                    </Form.Item>
                    <Form.Item label="Business" name="business">
                        <Transfer
                            dataSource={business}
                            titles={['Businesses', 'Selected']}
                            showSelectAll
                            targetKeys={targetKeys}
                            onChange={setTargetKeys}
                            render={(item: any) => item.companyName}
                            oneWay
                            listStyle={{
                                width: '50%',
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        name="comment"
                        label="Comment (optional)"
                    >
                        <TextArea
                            rows={4}
                            showCount
                            maxLength={250}
                        />
                    </Form.Item>
                    <Button
                        block
                        type="primary"
                        htmlType="submit"
                        style={{ marginTop: 16 }}
                    >
                        Create Invoice
                    </Button>
                </Form>

            </Space>
        </Modal>
    );
};

export default NewInvoice;
